import queryString from 'query-string';
import { ApiPathParam } from '../models/SearchParamSchema';
import { SubjectDetails } from '../api/candidate/exam/model';

interface ParseLocationSearchReturn {
  [key: string]: string | undefined;
}

type EmptyString = string | null | undefined;

/**
 * Parse location.search into object of key value pair. e.g. ?key1=value1&key2=value2
 * returns { key1: value1, key2: value2 }
 *
 * To use this function, ensures that URL query parameters only contain key value pairs. i.e. no array format like
 * ?key[]=value.
 *
 * If there are repeating keys, the value of the last repeated key will be returned.
 * e.g. ?key=value1&key=value2 will return { key: value2 }
 *
 * @param locationSearch location.search from useLocation() hook
 * @return an object containing key value pairs parsed from location.search
 */
export const parseLocationSearch = (locationSearch: string): ParseLocationSearchReturn => {
  const getStringValue = (value?: string | (string | null)[] | null): string | undefined => {
    if (typeof value === 'string') {
      return value;
    }
    if (Array.isArray(value) && value.length > 0) {
      // repeated keys in location.search scenario: only returns the last repeated not null key value pair
      let valueToBeReturned: string | undefined;
      value.slice().forEach((v) => {
        if (v !== null) {
          valueToBeReturned = v;
        }
      });
      return valueToBeReturned;
    }
    return undefined;
  };

  const parsedQuery = queryString.parse(locationSearch);
  const parsedQueryKeys = Object.keys(parsedQuery);

  let parsedQueryString = {};

  parsedQueryKeys.forEach((parsedQueryKey) => {
    parsedQueryString = {
      ...parsedQueryString,
      [parsedQueryKey]: getStringValue(parsedQuery[parsedQueryKey]),
    };
  });

  return parsedQueryString;
};

/**
 * Function to return empty string if the key of object is null or undefined, if not return the key string value
 * @param val val object
 * @param key key to check
 * @returns
 */
export const fallbackPropsEmptyString = (val: any, key: string) => {
  if (!val || !val[key]) {
    return '';
  }
  return val[key];
};

export const stringIsEmpty = (val: EmptyString): boolean =>
  val === null || val === undefined || val === '';

/**
 * Function to return empty string if the value is null or undefined, if not return value
 * @param val string value
 * @returns
 */
export const fallbackEmptyString = (val: EmptyString): string => {
  if (val === null || val === undefined) {
    return '';
  }
  return val;
};

/**
 * Return null if the string is empty (undefined, null, '') else return original value
 * @param stringValue
 */
export const getNullIfEmptyString = (stringValue?: string | null): string | null => {
  if (stringValue === undefined || stringValue === null || stringValue === '') {
    return null;
  }
  return stringValue;
};

/**
 * Return dash if the string is empty (undefined, null, '') else return original value
 * @param stringValue
 */
export const fallbackDashString = (val: EmptyString): string => {
  if (val === null || val === undefined || val === '') {
    return '-';
  }
  return val;
};

/**
 * Boolean check to pick A or B string to return
 * @param check boolean for checking
 * @param trueString return if true
 * @param falseString return if false
 * @returns
 */
export const booleanToString = (check: boolean, trueString: string, falseString: string) =>
  check ? trueString : falseString;

/**
 * Utility function to convert object to params
 * @param params
 * @returns
 */
export const formAPIParamPath = (params: ApiPathParam) => {
  let path = '?';
  if (params.order) {
    path += `order=${params.order}&`;
  }
  if (params.sort) {
    path += `sort=${params.sort}&`;
  }
  if (params.page) {
    path += `page=${params.page}&`;
  }
  if (params.pageSize) {
    path += `pageSize=${params.pageSize}`;
  }
  return path;
};

export const formAPIPageable = (params: ApiPathParam) => {
  let path = '?';
  if (params.page !== undefined) {
    path += `page=${params.page}&`;
  }
  if (params.pageSize) {
    path += `size=${params.pageSize}&`;
  }
  if (params.sort) {
    path += `sort=${params.sort}`;
    if (params.order) {
      path += `,${params.order}`;
    }
  } else {
    path = path.substring(0, path.length - 1);
  }
  return path;
};

export const formAPIPageWithoutSort = (params: ApiPathParam) => {
  let path = '?';
  if (params.page !== undefined) {
    path += `page=${params.page}&`;
  }
  if (params.pageSize) {
    path += `size=${params.pageSize}&`;
  }
  if (params.order) {
    path += `order=${params.order}`;
  } else {
    path = path.substring(0, path.length - 1);
  }
  return path;
};

export const formAPIPage = (params: ApiPathParam) => {
  let path = '?';
  if (params.page !== undefined) {
    path += `page=${params.page}&`;
  }
  if (params.pageSize) {
    path += `size=${params.pageSize}&`;
  }
  if (params.sort) {
    path += `sort=${params.sort}&`;
    if (params.order) {
      path += `order=${params.order}`;
    }
  } else {
    path = path.substring(0, path.length - 1);
  }
  return path;
};

export const formAPIToken = (path: string, token?: string) =>
  token ? `${path}?token=${token}` : path;

/**
 * Convert string to snake case
 * @param string
 * @returns
 */
export const toSnakeCase = (str: string) =>
  str
    .replace(/\d+/g, ' ')
    .split(/ |\B(?=[A-Z])/)
    .map((word) => word.toLowerCase())
    .join('_');

/**
 * Utils to format subject details model to string.
 *
 * @param args subject details
 */
export const setSubjectDetails = (args: SubjectDetails) => {
  const { subjectLevel, subjectName, subjectCode } = args;
  if (subjectLevel === 'NOT APPLICABLE' || !subjectLevel) {
    return `${subjectName} (${subjectCode})`;
  }
  return `${subjectLevel} ${subjectName} (${subjectCode})`;
};

/**
 * Compare text A and B and sort by target order.
 *
 * @param textA text A to be compared
 * @param textB text B to be compared
 * @param order sort order
 */
export const sortByAlphabeticalOrder = (
  textA: string,
  textB: string,
  order: 'asc' | 'desc'
): number => {
  const formattedA = textA.toUpperCase(); // ignore upper and lowercase
  const formattedB = textB.toUpperCase(); // ignore upper and lowercase
  if (formattedA < formattedB) {
    return order === 'asc' ? -1 : 1;
  }
  if (formattedA > formattedB) {
    return order === 'asc' ? 1 : -1;
  }
  return 0;
};

/**
 * Concat string array with comma/'and'.
 *
 * e.g.: ['tom', 'ken', bin] => 'tom, ken and bin'
 *
 * @param targets string array
 */
export const concatStringsWithComma = (targets: string[]) =>
  targets.join(', ').replace(/, ([^,]*)$/, ' and $1');

export const prefix0For2Digits = (value: string) => (value.length === 1 ? `0${value}` : value);
